import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, NumberInput, Switch, Table, TableBody, TableCell, TableHead,
  TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import {
  listTerminalUsers, upsertTerminalUser, deleteTerminalUser, setTerminalUserPassword,
  getPrinters, getTerminalStartup, provisionTerminal, downloadProvisionScript, createOperator,
} from '../api/terminalUsers';
import type {
  TerminalUser, TerminalUserInput, Printer, TerminalStartup, ProvisionTerminalInput,
  CreateOperatorInput,
} from '../api/terminalUsers';
import AccessDenied from '../components/AccessDenied';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import Modal from '../components/Modal';
import TableSkeleton from '../components/TableSkeleton';
import { canManageTerminalUsers, canProvisionOperator } from '../lib/roles';
import { t } from '../lib/i18n';
import type { User } from '../api/types';

const PRINTER_SLOT_COUNT = 11;

export default function TerminalUsers() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Owner or manager, mirroring the server's /api/admin/terminal-users gate.
  // Two roles qualify, so there's no single "required role" to name —
  // AccessDenied falls back to its generic "not available for your role" copy.
  if (!canManageTerminalUsers(user)) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} /></main>
      </div>
    );
  }
  return <TerminalUsersInner user={user} />;
}

type DialogState =
  | { mode: 'create' }
  | { mode: 'edit'; row: TerminalUser }
  | { mode: 'password'; row: TerminalUser }
  | { mode: 'provision'; row: TerminalUser }
  | { mode: 'operator'; row: TerminalUser }
  | null;

/** Transient header-level feedback line — success (green) or failure (red)
 *  — for actions that don't open a dialog of their own (the script download)
 *  or that close their dialog immediately on success (web password, terminal
 *  provisioning, operator creation). Auto-clears after a few seconds, same
 *  timing as Config.tsx's `note` state. */
type Note = { text: string; kind: 'ok' | 'err' };

function TerminalUsersInner({ user }: { user: User }) {
  const [rows, setRows] = useState<TerminalUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [dialogErr, setDialogErr] = useState<string | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [downloadingScript, setDownloadingScript] = useState(false);

  // Terminal/printer provisioning (startup.p) — gated behind writes_enabled
  // the same way the Anticipos panel gates its record form, on top of the
  // per-row requirement that there's a pacetty (startdev) to provision.
  const canProvision = !!user.features?.writes_enabled;
  // ERP green-screen operator provisioning — the most sensitive action in
  // this console. Requires BOTH writes_enabled AND the owner-only
  // operator:provision permission (canProvisionOperator).
  const canOperator = !!user.features?.writes_enabled && canProvisionOperator(user);

  function load() {
    setErr(null);
    listTerminalUsers()
      .then(setRows)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  }
  useEffect(() => { load(); }, []);

  function showNote(text: string, kind: Note['kind'] = 'ok') {
    setNote({ text, kind });
    window.setTimeout(() => setNote(null), 6000);
  }

  function openDialog(next: Exclude<DialogState, null>) {
    setDialogErr(null);
    setNote(null);
    setDialog(next);
  }

  async function handleSave(v: TerminalUserInput) {
    setDialogErr(null);
    try {
      await upsertTerminalUser(v);
      setDialog(null);
      load();
    } catch (e) {
      setDialogErr(e instanceof Error ? e.message : t.terminalUsers.errors.generic);
    }
  }

  async function handleDelete(row: TerminalUser) {
    if (!confirm(t.terminalUsers.confirmDelete.replace('{username}', row.username))) return;
    try {
      await deleteTerminalUser(row.username);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t.terminalUsers.errors.generic);
    }
  }

  async function handleSetPassword(row: TerminalUser, password: string) {
    setDialogErr(null);
    try {
      await setTerminalUserPassword(row.username, password);
      setDialog(null);
      showNote(t.terminalUsers.webPasswordSet.replace('{username}', row.username));
      load();
    } catch (e) {
      setDialogErr(e instanceof Error ? e.message : t.terminalUsers.errors.generic);
    }
  }

  async function handleProvision(row: TerminalUser, body: ProvisionTerminalInput) {
    setDialogErr(null);
    try {
      await provisionTerminal(body);
      setDialog(null);
      showNote(t.terminalUsers.provision.saved.replace('{startdev}', row.pacetty));
      // No load() — startup.p config isn't part of the row list, so there's
      // nothing in the table to refresh.
    } catch (e) {
      setDialogErr(e instanceof Error ? e.message : t.terminalUsers.errors.generic);
    }
  }

  async function handleCreateOperator(input: CreateOperatorInput) {
    setDialogErr(null);
    try {
      await createOperator(input);
      setDialog(null);
      showNote(t.terminalUsers.operator.created.replace('{userid}', input.userid));
      // No load() — the ERP operator login isn't part of the registry row
      // list, so there's nothing in the table to refresh.
    } catch (e) {
      setDialogErr(e instanceof Error ? e.message : t.terminalUsers.errors.generic);
    }
  }

  async function handleDownloadScript() {
    setDownloadingScript(true);
    try {
      await downloadProvisionScript();
      showNote(t.terminalUsers.provisionScript.downloaded);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t.terminalUsers.errors.generic;
      showNote(t.terminalUsers.provisionScript.errorPrefix + msg, 'err');
    } finally {
      setDownloadingScript(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Title>{t.terminalUsers.pageTitle}</Title>
            <Text className="text-tremor-content text-sm">{t.terminalUsers.pageSubtitle}</Text>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <Button variant="secondary" loading={downloadingScript} onClick={handleDownloadScript}>
                {t.terminalUsers.provisionScript.button}
              </Button>
              <Button onClick={() => openDialog({ mode: 'create' })}>
                ＋ {t.terminalUsers.addUser}
              </Button>
            </div>
            <Text className="text-tremor-content text-xs">{t.terminalUsers.provisionScript.hint}</Text>
          </div>
        </div>

        {note && (
          <Text className={note.kind === 'ok' ? 'text-emerald-700 dark:text-emerald-400 text-sm' : 'text-red-700 dark:text-red-400 text-sm'}>
            {note.text}
          </Text>
        )}

        {err && <ErrorRetry message={err} onRetry={load} />}

        {!err && rows === null && <TableSkeleton rows={6} cols={4} />}
        {!err && rows !== null && rows.length === 0 && (
          <EmptyState message={t.terminalUsers.empty} />
        )}
        {!err && rows !== null && rows.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.terminalUsers.columns.username}</TableHeaderCell>
                  <TableHeaderCell>{t.terminalUsers.columns.fullName}</TableHeaderCell>
                  <TableHeaderCell>{t.terminalUsers.columns.pacetty}</TableHeaderCell>
                  <TableHeaderCell>{t.terminalUsers.columns.status}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.terminalUsers.columns.actions}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.username}>
                    <TableCell className="font-medium">{r.username}</TableCell>
                    <TableCell>{r.full_name || '—'}</TableCell>
                    <TableCell>{r.pacetty || '—'}</TableCell>
                    <TableCell>
                      <Badge color={r.status === 'active' ? 'green' : 'gray'}>
                        {r.status === 'active' ? t.terminalUsers.status.active : t.terminalUsers.status.inactive}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="xs" variant="light" onClick={() => openDialog({ mode: 'edit', row: r })}>
                        {t.terminalUsers.actions.edit}
                      </Button>
                      <Button size="xs" variant="light" onClick={() => openDialog({ mode: 'password', row: r })}>
                        {t.terminalUsers.actions.webPassword}
                      </Button>
                      {canProvision && r.pacetty && (
                        <Button size="xs" variant="light" onClick={() => openDialog({ mode: 'provision', row: r })}>
                          {t.terminalUsers.actions.provision}
                        </Button>
                      )}
                      {canOperator && (
                        <Button size="xs" variant="light" onClick={() => openDialog({ mode: 'operator', row: r })}>
                          {t.terminalUsers.actions.operator}
                        </Button>
                      )}
                      <Button size="xs" variant="light" color="red" onClick={() => handleDelete(r)}>
                        {t.terminalUsers.actions.delete}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </main>

      {dialog?.mode === 'create' && (
        <TerminalUserFormDialog
          mode="create"
          onSubmit={handleSave}
          onCancel={() => setDialog(null)}
          errorText={dialogErr}
        />
      )}
      {dialog?.mode === 'edit' && (
        <TerminalUserFormDialog
          mode="edit"
          initial={dialog.row}
          onSubmit={handleSave}
          onCancel={() => setDialog(null)}
          errorText={dialogErr}
        />
      )}
      {dialog?.mode === 'password' && (
        <TerminalUserPasswordDialog
          row={dialog.row}
          onSubmit={(pw) => handleSetPassword(dialog.row, pw)}
          onCancel={() => setDialog(null)}
          errorText={dialogErr}
        />
      )}
      {dialog?.mode === 'provision' && (
        <TerminalProvisionDialog
          row={dialog.row}
          onSubmit={(body) => handleProvision(dialog.row, body)}
          onCancel={() => setDialog(null)}
          errorText={dialogErr}
        />
      )}
      {dialog?.mode === 'operator' && (
        <OperatorProvisionDialog
          row={dialog.row}
          onSubmit={handleCreateOperator}
          onCancel={() => setDialog(null)}
          errorText={dialogErr}
        />
      )}
    </div>
  );
}

interface FormProps {
  mode: 'create' | 'edit';
  initial?: TerminalUser | null;
  onSubmit: (v: TerminalUserInput) => Promise<void> | void;
  onCancel: () => void;
  errorText?: string | null;
}

/**
 * Create/edit form for one terminal-user row. Same shared-Modal pattern as
 * UserFormDialog — kept local to this page since it's only used here.
 * Username is editable only on create: the write endpoint upserts keyed by
 * username, so changing it on edit would silently create a second row
 * instead of updating the one being edited. `os_user` isn't a field in this
 * form (nothing here drives it); it's passed through unchanged from
 * `initial` on edit and sent empty on create.
 */
function TerminalUserFormDialog({ mode, initial, onSubmit, onCancel, errorText }: FormProps) {
  const [username, setUsername] = useState(initial?.username ?? '');
  const [fullName, setFullName] = useState(initial?.full_name ?? '');
  const [pacetty, setPacetty] = useState(initial?.pacetty ?? '');
  const [status, setStatus] = useState<'active' | 'inactive'>(initial?.status ?? 'active');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const titleText = mode === 'create' ? t.terminalUsers.dialog.titleCreate : t.terminalUsers.dialog.titleEdit;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(null);
    if (username.trim().length === 0) {
      setLocalErr(t.terminalUsers.dialog.username);
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        username: username.trim(),
        full_name: fullName.trim(),
        pacetty: pacetty.trim(),
        os_user: initial?.os_user ?? '',
        status,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} ariaLabel={titleText} panelClassName="max-w-md p-5">
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-lg font-semibold text-tremor-content-strong">{titleText}</h2>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.dialog.username}</span>
          <TextInput
            value={username}
            onValueChange={setUsername}
            disabled={mode === 'edit'}
            required
          />
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.dialog.fullName}</span>
          <TextInput value={fullName} onValueChange={setFullName} />
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.dialog.pacetty}</span>
          <TextInput value={pacetty} onValueChange={setPacetty} placeholder="net149" />
          <span className="text-xs text-tremor-content block mt-1">{t.terminalUsers.dialog.pacettyHint}</span>
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.dialog.status}</span>
          {/* Native <select> — same reasoning as UserFormDialog's role select:
              Tremor's Select popover stacks under the modal backdrop's z-40. */}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
            className="w-full rounded-tremor-default border border-tremor-border bg-tremor-background py-2 px-3 text-sm text-tremor-content-strong focus:border-tremor-brand focus:outline-none focus:ring-2 focus:ring-tremor-brand-muted"
          >
            <option value="active">{t.terminalUsers.status.active}</option>
            <option value="inactive">{t.terminalUsers.status.inactive}</option>
          </select>
        </label>

        {(localErr || errorText) && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 dark:text-red-300 dark:bg-red-950 dark:border-red-900 rounded px-3 py-2">
            {localErr ?? errorText}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="light" onClick={onCancel} disabled={busy}>
            {t.terminalUsers.dialog.cancel}
          </Button>
          <Button type="submit" loading={busy}>
            {t.terminalUsers.dialog.save}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface PasswordFormProps {
  row: TerminalUser;
  onSubmit: (password: string) => Promise<void> | void;
  onCancel: () => void;
  errorText?: string | null;
}

/**
 * Sets the operator's pacebi-managed web-terminal login password — the
 * account used to sign into this SPA's Pacesetter web terminal — which is
 * separate from the operator's Pacesetter/ERP login typed at the green
 * screen itself. Same shared-Modal pattern as TerminalUserFormDialog.
 * Submit stays disabled until both fields are filled, match, and clear the
 * 8-char floor, mirroring the server's own >=8 rule.
 */
function TerminalUserPasswordDialog({ row, onSubmit, onCancel, errorText }: PasswordFormProps) {
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirmPw.length > 0 && password !== confirmPw;
  const canSubmit = password.length >= 8 && password === confirmPw;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(null);
    if (password.length < 8) {
      setLocalErr(t.terminalUsers.dialog.passwordTooShort);
      return;
    }
    if (password !== confirmPw) {
      setLocalErr(t.terminalUsers.dialog.passwordMismatch);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(password);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onCancel}
      ariaLabel={t.terminalUsers.dialog.webPasswordTitle.replace('{username}', row.username)}
      panelClassName="max-w-md p-5"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-lg font-semibold text-tremor-content-strong">
          {t.terminalUsers.dialog.webPasswordTitle.replace('{username}', row.username)}
        </h2>
        <Text className="text-tremor-content text-xs">{t.terminalUsers.dialog.webPasswordHint}</Text>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.dialog.newPassword}</span>
          <TextInput
            type="password"
            value={password}
            onValueChange={setPassword}
            placeholder={t.terminalUsers.dialog.passwordPlaceholder}
            required
          />
          {tooShort && (
            <span className="text-xs text-amber-600 dark:text-amber-400 block mt-1">
              {t.terminalUsers.dialog.passwordTooShort}
            </span>
          )}
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.dialog.confirmPassword}</span>
          <TextInput
            type="password"
            value={confirmPw}
            onValueChange={setConfirmPw}
            required
          />
          {mismatch && (
            <span className="text-xs text-amber-600 dark:text-amber-400 block mt-1">
              {t.terminalUsers.dialog.passwordMismatch}
            </span>
          )}
        </label>

        {(localErr || errorText) && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 dark:text-red-300 dark:bg-red-950 dark:border-red-900 rounded px-3 py-2">
            {localErr ?? errorText}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="light" onClick={onCancel} disabled={busy}>
            {t.terminalUsers.dialog.cancel}
          </Button>
          <Button type="submit" loading={busy} disabled={!canSubmit}>
            {t.terminalUsers.dialog.save}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface ProvisionFormProps {
  row: TerminalUser;
  onSubmit: (v: ProvisionTerminalInput) => Promise<void> | void;
  onCancel: () => void;
  errorText?: string | null;
}

/**
 * Provisions a register's store + printer routing — the ERP startup.p row
 * keyed by `startdev` (this row's PACETTY). On open, loads the printer
 * catalog and the existing startup row (if any) in parallel; prefills every
 * field from the existing row when `found`, otherwise starts from zeros/
 * blanks. The caller (TerminalUsersInner) only renders this when
 * writes_enabled is on and the row has a non-empty pacetty, so this
 * component assumes both already hold.
 */
function TerminalProvisionDialog({ row, onSubmit, onCancel, errorText }: ProvisionFormProps) {
  const [printers, setPrinters] = useState<Printer[] | null>(null);
  const [startup, setStartup] = useState<TerminalStartup | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [sid, setSid] = useState(0);
  const [slots, setSlots] = useState<number[]>(() => Array(PRINTER_SLOT_COUNT).fill(0));
  const [prlocal, setPrlocal] = useState('');
  const [prnorecpt, setPrnorecpt] = useState(false);
  const [localForm, setLocalForm] = useState('');
  const [cashbox, setCashbox] = useState(0);

  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([getPrinters(), getTerminalStartup(row.pacetty)])
      .then(([pr, su]) => {
        if (!alive) return;
        setPrinters(pr);
        setStartup(su);
        if (su.found) {
          setSid(su.sid);
          setSlots(su.pr_array.length === PRINTER_SLOT_COUNT ? su.pr_array : Array(PRINTER_SLOT_COUNT).fill(0));
          setPrlocal(su.prlocal ?? '');
          setPrnorecpt(su.prnorecpt === 1);
          setLocalForm(su.local_form ?? '');
          setCashbox(su.cashbox ?? 0);
        }
      })
      .catch((e: Error) => { if (alive) setLoadErr(e.message ?? t.terminalUsers.errors.generic); });
    return () => { alive = false; };
  }, [row.pacetty]);

  const ready = printers !== null && startup !== null;

  function updateSlot(i: number, value: number) {
    setSlots((prev) => prev.map((v, idx) => (idx === i ? value : v)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(null);
    setBusy(true);
    try {
      await onSubmit({
        startdev: row.pacetty,
        sid,
        printers: slots,
        prlocal: prlocal.trim(),
        prnorecpt,
        cashbox,
        local_form: localForm.trim(),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onCancel}
      ariaLabel={t.terminalUsers.provision.title.replace('{startdev}', row.pacetty)}
      panelClassName="max-w-2xl p-5"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-lg font-semibold text-tremor-content-strong">
          {t.terminalUsers.provision.title.replace('{startdev}', row.pacetty)}
        </h2>
        <Text className="text-tremor-content text-xs">{t.terminalUsers.provision.hint}</Text>

        {!ready && !loadErr && (
          <Text className="text-tremor-content text-sm">{t.terminalUsers.provision.loading}</Text>
        )}

        {loadErr && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 dark:text-red-300 dark:bg-red-950 dark:border-red-900 rounded px-3 py-2">
            {loadErr}
          </div>
        )}

        {ready && printers && (
          <>
            <label className="block text-sm max-w-[10rem]">
              <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.provision.sid}</span>
              <NumberInput min={0} value={sid} onValueChange={(n) => setSid(typeof n === 'number' ? n : 0)} />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.terminalUsers.provision.slots.map((label, i) => (
                <label key={i} className="block text-sm">
                  <span className="text-tremor-content text-xs mb-1 block">{`[${i + 1}] ${label}`}</span>
                  <select
                    value={slots[i]}
                    onChange={(e) => updateSlot(i, Number(e.target.value))}
                    className="w-full rounded-tremor-default border border-tremor-border bg-tremor-background py-2 px-3 text-sm text-tremor-content-strong focus:border-tremor-brand focus:outline-none focus:ring-2 focus:ring-tremor-brand-muted"
                  >
                    <option value={0}>{t.terminalUsers.provision.none}</option>
                    {printers.map((p) => (
                      <option key={p.prc_num} value={p.prc_num}>{`${p.prc_type} · ${p.prc_dev}`}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            <label className="block text-sm">
              <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.provision.prlocal}</span>
              <TextInput value={prlocal} onValueChange={setPrlocal} />
            </label>

            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <Switch checked={prnorecpt} onChange={setPrnorecpt} />
              {t.terminalUsers.provision.prnorecpt}
            </label>

            <label className="block text-sm">
              <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.provision.localForm}</span>
              <TextInput value={localForm} onValueChange={setLocalForm} />
            </label>
          </>
        )}

        {(localErr || errorText) && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 dark:text-red-300 dark:bg-red-950 dark:border-red-900 rounded px-3 py-2">
            {localErr ?? errorText}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="light" onClick={onCancel} disabled={busy}>
            {t.terminalUsers.dialog.cancel}
          </Button>
          <Button type="submit" loading={busy} disabled={!ready}>
            {t.terminalUsers.dialog.save}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface OperatorFormProps {
  row: TerminalUser;
  onSubmit: (v: CreateOperatorInput) => Promise<void> | void;
  onCancel: () => void;
  errorText?: string | null;
}

/**
 * Creates an ERP green-screen operator login (a Progress _User
 * authenticated by SETUSERID — the account typed at the Pacesetter login
 * prompt itself). NOT the same as TerminalUserPasswordDialog, which sets
 * the separate pacebi-managed WEB login used to sign into this SPA's
 * Pacesetter web terminal — the title/hint copy here calls that out
 * explicitly since the two are easy to conflate. `userid` defaults to the
 * row's os_user if set, else its username — both are just starting points,
 * fully editable. Same password/confirm validation UX as
 * TerminalUserPasswordDialog (>=8 chars, must match). The caller
 * (TerminalUsersInner) only renders this when writes_enabled is on AND the
 * user has the owner-only operator:provision permission — the single most
 * sensitive action in this console.
 */
function OperatorProvisionDialog({ row, onSubmit, onCancel, errorText }: OperatorFormProps) {
  const [userid, setUserid] = useState(row.os_user || row.username);
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirmPw.length > 0 && password !== confirmPw;
  const canSubmit = userid.trim().length > 0 && password.length >= 8 && password === confirmPw;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(null);
    if (userid.trim().length === 0) {
      setLocalErr(t.terminalUsers.operator.useridRequired);
      return;
    }
    if (password.length < 8) {
      setLocalErr(t.terminalUsers.dialog.passwordTooShort);
      return;
    }
    if (password !== confirmPw) {
      setLocalErr(t.terminalUsers.dialog.passwordMismatch);
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ userid: userid.trim(), password });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onCancel}
      ariaLabel={t.terminalUsers.operator.title.replace('{username}', row.username)}
      panelClassName="max-w-md p-5"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-lg font-semibold text-tremor-content-strong">
          {t.terminalUsers.operator.title.replace('{username}', row.username)}
        </h2>
        <Text className="text-tremor-content text-xs">{t.terminalUsers.operator.hint}</Text>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.operator.userid}</span>
          <TextInput value={userid} onValueChange={setUserid} required />
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.operator.password}</span>
          <TextInput
            type="password"
            value={password}
            onValueChange={setPassword}
            placeholder={t.terminalUsers.dialog.passwordPlaceholder}
            required
          />
          {tooShort && (
            <span className="text-xs text-amber-600 dark:text-amber-400 block mt-1">
              {t.terminalUsers.dialog.passwordTooShort}
            </span>
          )}
        </label>

        <label className="block text-sm">
          <span className="text-tremor-content text-xs mb-1 block">{t.terminalUsers.dialog.confirmPassword}</span>
          <TextInput
            type="password"
            value={confirmPw}
            onValueChange={setConfirmPw}
            required
          />
          {mismatch && (
            <span className="text-xs text-amber-600 dark:text-amber-400 block mt-1">
              {t.terminalUsers.dialog.passwordMismatch}
            </span>
          )}
        </label>

        {(localErr || errorText) && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 dark:text-red-300 dark:bg-red-950 dark:border-red-900 rounded px-3 py-2">
            {localErr ?? errorText}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="light" onClick={onCancel} disabled={busy}>
            {t.terminalUsers.dialog.cancel}
          </Button>
          <Button type="submit" loading={busy} disabled={!canSubmit}>
            {t.terminalUsers.dialog.save}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
